'use client';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Container, Section } from '@layout';
import { Button, Card, Input } from '@ui';
import { IconChevronRight, IconCloseX, IconSearch } from '@icons';

export type SearchSuggestItem = {
  label: string;
  /** Optional metadata (e.g. **`#anchor`** for in-page scroll, kept off the visible row when it starts with `#`). */
  meta?: string;
  /** Optional run handler called before **`onSelect`** when the row is picked. */
  run?: () => void;
};

export type SearchSuggestHandle = {
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
  isOpen: () => boolean;
};

interface SearchSuggestProps {
  /** Filterable items. Leave empty when wiring async results later. */
  suggestions?: SearchSuggestItem[];
  /** Controlled open state. When omitted, the component manages its own state via the imperative handle (**`SearchSuggestHandle`**). */
  open?: boolean;
  /** Notified on every open/close (controlled or uncontrolled). */
  onOpenChange?: (open: boolean) => void;
  /** Override the built-in filter (`label.includes` + `meta.includes`). */
  filter?: (query: string, items: SearchSuggestItem[]) => SearchSuggestItem[];
  /** Hide the suggestions card until the query has at least this many chars (default `0` — show all on focus). */
  minLength?: number;
  /** Cap rendered items. */
  maxItems?: number;
  placeholder?: string;
  /** **`aria-label`** for the field; also the search landmark label. */
  ariaLabel?: string;
  /** Label rendered above the list in the panel. */
  suggestionsLabel?: string;
  /** Optional **`id`** on the root (defaults to `search-suggest`). */
  id?: string;
  /** Extra classes on the root. */
  className?: string;
  /** Called after a suggestion is selected (after **`item.run`** if present). */
  onSelect?: (item: SearchSuggestItem) => void;
  /** Show the trailing **`>`** chevron on each option, signaling "select / drill into this" (default true). */
  showTrailingChevron?: boolean;
  /** **`aria-label`** for the close button. */
  closeAriaLabel?: string;
}

const defaultSuggestions: SearchSuggestItem[] = [];

function SearchSuggestInner(props: SearchSuggestProps, ref: React.Ref<SearchSuggestHandle>) {
  const {
    suggestions = defaultSuggestions,
    open: openProp,
    onOpenChange,
    filter,
    minLength = 0,
    maxItems,
    placeholder = 'Search…',
    ariaLabel = 'Search',
    suggestionsLabel = 'Suggestions',
    id = 'search-suggest',
    className,
    onSelect,
    showTrailingChevron = true,
    closeAriaLabel = 'Close search',
  } = props;

  const isControlled = typeof openProp === 'boolean';
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? !!openProp : internalOpen;

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [hasFocus, setHasFocus] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const setOpenState = useCallback(
    (next: boolean) => {
      if (isControlled) {
        onOpenChange?.(next);
      } else {
        setInternalOpen(next);
        onOpenChange?.(next);
      }
      if (!next) {
        setQuery('');
        setHasFocus(false);
        setActive(0);
      }
    },
    [isControlled, onOpenChange]
  );

  useImperativeHandle(
    ref,
    () => ({
      openSearch: () => setOpenState(true),
      closeSearch: () => setOpenState(false),
      toggleSearch: () => setOpenState(!isOpen),
      isOpen: () => isOpen,
    }),
    [setOpenState, isOpen]
  );

  const filtered = useMemo<SearchSuggestItem[]>(() => {
    const q = query.trim().toLowerCase();
    let res: SearchSuggestItem[];
    if (filter) {
      res = filter(q, suggestions);
    } else if (q.length < minLength) {
      res = [];
    } else if (q) {
      res = suggestions.filter(
        (s) => s.label.toLowerCase().includes(q) || (!!s.meta && s.meta.toLowerCase().includes(q))
      );
    } else {
      res = suggestions;
    }
    return typeof maxItems === 'number' ? res.slice(0, maxItems) : res;
  }, [filter, maxItems, minLength, query, suggestions]);

  useEffect(() => {
    setActive(0);
  }, [filtered]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(handle);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpenState(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, setOpenState]);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!rootRef.current || !target) return;
      if (rootRef.current.contains(target)) return;
      setOpenState(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen, setOpenState]);

  const handleSelect = useCallback(
    (item: SearchSuggestItem) => {
      if (typeof item.run === 'function') item.run();
      onSelect?.(item);
      setOpenState(false);
    },
    [onSelect, setOpenState]
  );

  const showList = hasFocus && filtered.length > 0;

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (filtered.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((a) => (a + 1) % filtered.length);
      setHasFocus(true);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((a) => (a - 1 + filtered.length) % filtered.length);
      setHasFocus(true);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const pick = filtered[active];
      if (pick) handleSelect(pick);
    }
  };

  if (!isOpen) return null;

  const listId = `${id}-list`;
  const listLabelId = `${id}-list-label`;

  return (
    <Section
      sectionID={id}
      className={['search-suggest', showList ? 'open' : '', className].filter(Boolean).join(' ')}
      role='search'
      aria-label={ariaLabel}
    >
      <Container containerClasses='search-suggest-container'>
        <div ref={rootRef} className='search-suggest-tracker'>
          <div className='search-suggest-field'>
            <span className='search-suggest-icon' aria-hidden='true'>
              <IconSearch className='ts-ic-sm' />
            </span>
            <Input
              ref={inputRef}
              id={`${id}-input`}
              className='search-suggest-input'
              type='text'
              autoComplete='off'
              role='combobox'
              aria-controls={listId}
              aria-expanded={showList}
              aria-haspopup='listbox'
              aria-autocomplete='list'
              aria-label={ariaLabel}
              placeholder={placeholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHasFocus(true);
              }}
              onFocus={() => setHasFocus(true)}
              onKeyDown={onInputKeyDown}
            />
            <Button
              className='btn-close search-suggest-close'
              aria-label={closeAriaLabel}
              onClick={() => setOpenState(false)}
              as='button'
              type='button'
              icon={<IconCloseX />}
            />
          </div>

          {showList ? (
            <Card className='search-suggest-panel' role='presentation'>
              <div className='search-suggest-panel-label' id={listLabelId}>
                {suggestionsLabel}
              </div>
              <ul
                id={listId}
                className='search-suggest-list'
                role='listbox'
                aria-labelledby={listLabelId}
              >
                {filtered.map((item, i) => (
                  <li
                    key={`${item.meta ?? 'run'}:${item.label}`}
                    className={['search-suggest-option', i === active ? 'active' : '']
                      .filter(Boolean)
                      .join(' ')}
                    role='option'
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => handleSelect(item)}
                  >
                    <span className='search-suggest-option-label'>{item.label}</span>
                    {item.meta && !/^#/.test(item.meta) ? (
                      <span className='search-suggest-option-meta'>{item.meta}</span>
                    ) : null}
                    {showTrailingChevron ? (
                      <span className='search-suggest-option-chevron' aria-hidden='true'>
                        <IconChevronRight className='ts-ic-sm' />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </Container>
    </Section>
  );
}

const SearchSuggest = forwardRef<SearchSuggestHandle, SearchSuggestProps>(SearchSuggestInner);
SearchSuggest.displayName = 'SearchSuggest';

export default SearchSuggest;
